/* ==========================================================================
   VONA — INTERACTION LAYER
   ==========================================================================

   Written as custom elements to match how Dawn already does things, so the
   theme has one pattern rather than two. No framework, no dependencies.

   <vona-menu>       mega-dropdown nav: hover on desktop, tap on mobile
   <vona-accordion>  single-open accordion (FAQ, product details)
   <vona-mobile-nav> slide-in panel below the desktop breakpoint
   ========================================================================== */

(() => {
  'use strict';

  const DESKTOP = window.matchMedia('(min-width: 990px)');

  /* ------------------------------------------------------------------
     MEGA-DROPDOWN
     One <vona-menu> wraps the whole nav so only one panel is open at a
     time and leaving the bar closes whatever is open.
     ------------------------------------------------------------------ */
  class VonaMenu extends HTMLElement {
    connectedCallback() {
      this.triggers = [...this.querySelectorAll('[data-vona-trigger]')];
      this.panels = [...this.querySelectorAll('[data-vona-panel]')];
      this.open = null;

      this.triggers.forEach((trigger) => {
        const name = trigger.dataset.vonaTrigger;

        // Desktop opens on intent; mobile has no hover, so tap handles it.
        trigger.addEventListener('mouseenter', () => {
          if (DESKTOP.matches) this.show(name);
        });

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle(name);
        });

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.hide();
        });
      });

      // Leaving the whole header closes the panel — matches the prototype's
      // onMouseLeave on the nav wrapper.
      this.addEventListener('mouseleave', () => {
        if (DESKTOP.matches) this.hide();
      });

      this._onDocClick = (e) => {
        if (!this.contains(e.target)) this.hide();
      };
      this._onEsc = (e) => {
        if (e.key === 'Escape') this.hide();
      };
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onEsc);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onEsc);
    }

    show(name) {
      if (this.open === name) return;
      this.open = name;
      this.panels.forEach((p) => {
        p.hidden = p.dataset.vonaPanel !== name;
      });
      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', String(t.dataset.vonaTrigger === name));
      });
    }

    hide() {
      if (this.open === null) return;
      this.open = null;
      this.panels.forEach((p) => {
        p.hidden = true;
      });
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    }

    toggle(name) {
      this.open === name ? this.hide() : this.show(name);
    }
  }
  customElements.define('vona-menu', VonaMenu);

  /* ------------------------------------------------------------------
     ACCORDION
     Single-open. The "+" rotates 45deg to an "x" via CSS on [aria-expanded].
     ------------------------------------------------------------------ */
  class VonaAccordion extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-vona-acc-item]')];

      this.items.forEach((item) => {
        const btn = item.querySelector('[data-vona-acc-trigger]');
        const panel = item.querySelector('[data-vona-acc-panel]');
        if (!btn || !panel) return;

        btn.addEventListener('click', () => {
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          if (this.dataset.single !== 'false') this.closeAll();
          if (!isOpen) {
            btn.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
          }
        });
      });
    }

    closeAll() {
      this.items.forEach((item) => {
        const btn = item.querySelector('[data-vona-acc-trigger]');
        const panel = item.querySelector('[data-vona-acc-panel]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    }
  }
  customElements.define('vona-accordion', VonaAccordion);

  /* ------------------------------------------------------------------
     MOBILE NAV
     ------------------------------------------------------------------ */
  class VonaMobileNav extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-vona-mobile-toggle]');
      this.panel = this.querySelector('[data-vona-mobile-panel]');
      if (!this.toggleBtn || !this.panel) return;

      this.toggleBtn.addEventListener('click', () => {
        const isOpen = this.toggleBtn.getAttribute('aria-expanded') === 'true';
        this.toggleBtn.setAttribute('aria-expanded', String(!isOpen));
        this.panel.hidden = isOpen;
        document.body.style.overflow = isOpen ? '' : 'hidden';
      });

      // Nested disclosure groups inside the mobile panel.
      this.querySelectorAll('[data-vona-mobile-group]').forEach((group) => {
        const btn = group.querySelector('[data-vona-mobile-group-trigger]');
        const body = group.querySelector('[data-vona-mobile-group-panel]');
        if (!btn || !body) return;
        btn.addEventListener('click', () => {
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!isOpen));
          body.hidden = isOpen;
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.toggleBtn.getAttribute('aria-expanded') === 'true') {
          this.toggleBtn.click();
        }
      });
    }
  }
  customElements.define('vona-mobile-nav', VonaMobileNav);
})();
